import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { apiError, recruitersAdminApi } from "../api";
import { dueLabel, money, pct } from "../format";
import { Field, SectionTitle } from "../ui";
import type { ChecklistTemplateItem, DueRule, ProgramSettings, ProgramTerms } from "../types";
import { LoadingBlock, ErrorBlock } from "../../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

/**
 * Condiciones del programa (iguales para todos), datos de Go Up Cloud SpA para
 * la boleta y plantillas del checklist (Anexo 1). Cambiar el checklist sube la
 * versión: los eventos ya creados conservan la suya.
 */
export function SettingsPane() {
  const q = useQuery({ queryKey: ["recruiters", "settings"], queryFn: recruitersAdminApi.settings });
  if (q.isLoading) return <LoadingBlock label="Cargando configuración…" />;
  if (q.isError || !q.data) return <ErrorBlock onRetry={() => q.refetch()} />;
  return <SettingsForm initial={q.data.settings} />;
}

type TermField = { key: keyof ProgramTerms; label: string; kind: "money" | "pct" | "int"; suffix?: string; help?: string };

const TERM_FIELDS: TermField[] = [
  { key: "originationFee", label: "Fee de originación", kind: "money", help: "Una vez por productor, al vender su primer evento." },
  { key: "activationRate", label: "Tasa de activación", kind: "pct" },
  { key: "activationEvents", label: "Eventos de activación", kind: "int", suffix: "eventos", help: "Del 1 hasta este número va la tasa de activación." },
  { key: "maintenanceRate", label: "Tasa de mantención", kind: "pct" },
  { key: "attributionExpiryDays", label: "Caducidad sin actividad", kind: "int", suffix: "días corridos" },
  { key: "retentionRate", label: "Retención de la boleta", kind: "pct" },
  { key: "reportBusinessDays", label: "Plazo del reporte", kind: "int", suffix: "días hábiles" },
  { key: "cureBusinessDays", label: "Plazo para subsanar", kind: "int", suffix: "días hábiles" },
  { key: "reviewWindowDays", label: "Mixcore observa dentro de", kind: "int", suffix: "días", help: "Si no observa en ese plazo, el checklist queda aprobado." },
  { key: "precontactHours", label: "Contacto previo", kind: "int", suffix: "horas antes" },
  { key: "simultaneousNoticeHours", label: "Aviso de evento simultáneo", kind: "int", suffix: "horas antes" },
];

function SettingsForm({ initial }: { initial: ProgramSettings }) {
  const { push } = useToast();
  const qc = useQueryClient();
  const canEdit = usePermission(PERMISSIONS.RECRUITERS_MANAGE);
  const [s, setS] = useState<ProgramSettings>(initial);
  useEffect(() => setS(initial), [initial]);

  const mut = useMutation({
    mutationFn: () =>
      recruitersAdminApi.saveSettings({
        terms: s.terms,
        company: s.company,
        agreementVersion: s.agreementVersion,
        checklist: { version: s.checklist.version, activation: s.checklist.activation, maintenance: s.checklist.maintenance },
      }),
    onSuccess: (r) => {
      push({
        kind: "success",
        message:
          r.settings.checklist.version !== initial.checklist.version
            ? `Guardado. El checklist pasa a la versión ${r.settings.checklist.version} para los eventos nuevos.`
            : "Configuración guardada.",
      });
      qc.invalidateQueries({ queryKey: ["recruiters"] });
    },
    onError: (err) => push({ kind: "error", message: apiError(err) }),
  });

  const setTerm = (k: keyof ProgramTerms, v: number) => setS((p) => ({ ...p, terms: { ...p.terms, [k]: v } }));
  const setCompany = (k: keyof ProgramSettings["company"], v: string) => setS((p) => ({ ...p, company: { ...p.company, [k]: v } }));
  const setItems = (stage: "activation" | "maintenance", items: ChecklistTemplateItem[]) =>
    setS((p) => ({ ...p, checklist: { ...p.checklist, [stage]: items } }));

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <SectionTitle>Condiciones del programa</SectionTitle>
        <div className="nx-card p-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TERM_FIELDS.map((f) => (
            <Field key={f.key} label={f.label} hint={f.help}>
              <TermInput field={f} value={s.terms[f.key]} disabled={!canEdit} onChange={(v) => setTerm(f.key, v)} />
            </Field>
          ))}
          <Field label="Versión del acuerdo" hint="Se guarda en cada reclutador nuevo.">
            <input className="nx-input" value={s.agreementVersion} disabled={!canEdit} onChange={(e) => setS((p) => ({ ...p, agreementVersion: e.target.value }))} />
          </Field>
        </div>
        <p className="text-xs text-fg-subtle">
          Hoy: {money(s.terms.originationFee)} por productor · {pct(s.terms.activationRate)} en los eventos 1 al {s.terms.activationEvents} ·{" "}
          {pct(s.terms.maintenanceRate)} desde el {s.terms.activationEvents + 1} · retención {pct(s.terms.retentionRate)}.
        </p>
      </section>

      <section className="space-y-3">
        <SectionTitle>Datos para la boleta de honorarios</SectionTitle>
        <div className="nx-card p-4 grid gap-4 sm:grid-cols-2">
          <Field label="Razón social">
            <input className="nx-input" value={s.company.name} disabled={!canEdit} onChange={(e) => setCompany("name", e.target.value)} />
          </Field>
          <Field label="RUT">
            <input className="nx-input" value={s.company.rut} disabled={!canEdit} onChange={(e) => setCompany("rut", e.target.value)} />
          </Field>
          <Field label="Giro">
            <input className="nx-input" value={s.company.giro} disabled={!canEdit} onChange={(e) => setCompany("giro", e.target.value)} />
          </Field>
          <Field label="Dirección">
            <input className="nx-input" value={s.company.address} disabled={!canEdit} onChange={(e) => setCompany("address", e.target.value)} />
          </Field>
        </div>
      </section>

      <section className="space-y-3">
        <SectionTitle>Checklist por evento · versión {s.checklist.version}</SectionTitle>
        <div className="grid gap-4 lg:grid-cols-2">
          <ChecklistEditor title="Activación (eventos 1 al 6)" items={s.checklist.activation} disabled={!canEdit} onChange={(it) => setItems("activation", it)} />
          <ChecklistEditor title="Mantención (desde el 7)" items={s.checklist.maintenance} disabled={!canEdit} onChange={(it) => setItems("maintenance", it)} />
        </div>
      </section>

      {canEdit && (
        <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-bg-base/90 backdrop-blur border-t border-border flex justify-end">
          <button type="button" className="nx-btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar configuración
          </button>
        </div>
      )}
    </div>
  );
}

function TermInput({ field, value, disabled, onChange }: { field: TermField; value: number; disabled: boolean; onChange: (v: number) => void }) {
  const shown = field.kind === "pct" ? Math.round(value * 10000) / 100 : value;
  return (
    <div className="flex items-center gap-2">
      {field.kind === "money" && <span className="text-sm text-fg-muted">$</span>}
      <input
        className="nx-input tabular-nums"
        type="number"
        inputMode="decimal"
        step={field.kind === "pct" ? "0.01" : "1"}
        min={0}
        value={Number.isFinite(shown) ? shown : 0}
        disabled={disabled}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (!Number.isFinite(n)) return;
          onChange(field.kind === "pct" ? n / 100 : Math.round(n));
        }}
      />
      <span className="text-xs text-fg-muted whitespace-nowrap">{field.kind === "pct" ? "%" : field.suffix ?? ""}</span>
    </div>
  );
}

const DUE_KINDS: { value: DueRule["kind"]; label: string }[] = [
  { value: "before_event_hours", label: "Horas antes del evento" },
  { value: "event_day", label: "El día del evento" },
  { value: "after_event_business_days", label: "Días hábiles después" },
  { value: "none", label: "Sin plazo" },
];

function slugKey(label: string, taken: string[]): string {
  const base =
    label
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 24) || "item";
  let key = /^[a-z]/.test(base) ? base : `i_${base}`;
  let n = 2;
  while (taken.includes(key)) key = `${base}_${n++}`;
  return key;
}

function ChecklistEditor({ title, items, disabled, onChange }: { title: string; items: ChecklistTemplateItem[]; disabled: boolean; onChange: (items: ChecklistTemplateItem[]) => void }) {
  const update = (i: number, patch: Partial<ChecklistTemplateItem>) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="nx-card p-4 space-y-3">
      <div className="font-semibold text-fg text-sm">{title}</div>
      <ol className="space-y-3">
        {items.map((it, i) => (
          <li key={it.key} className="rounded-md border border-border p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-fg-subtle tabular-nums w-5">{i + 1}</span>
              <input className="nx-input" value={it.label} disabled={disabled} onChange={(e) => update(i, { label: e.target.value })} />
              {!disabled && (
                <div className="flex shrink-0">
                  <button type="button" className="p-1.5 text-fg-subtle hover:text-fg" onClick={() => move(i, -1)} aria-label="Subir">
                    <ArrowUp size={14} />
                  </button>
                  <button type="button" className="p-1.5 text-fg-subtle hover:text-fg" onClick={() => move(i, 1)} aria-label="Bajar">
                    <ArrowDown size={14} />
                  </button>
                  <button type="button" className="p-1.5 text-fg-subtle hover:text-danger" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Quitar">
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
            <textarea className="nx-input min-h-[52px] text-sm" value={it.help} disabled={disabled} placeholder="Qué tiene que hacer el reclutador" onChange={(e) => update(i, { help: e.target.value })} />
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="nx-input w-auto"
                value={it.due.kind}
                disabled={disabled}
                onChange={(e) => {
                  const kind = e.target.value as DueRule["kind"];
                  update(i, {
                    due:
                      kind === "before_event_hours"
                        ? { kind, hours: 48 }
                        : kind === "after_event_business_days"
                          ? { kind, days: 5 }
                          : ({ kind } as DueRule),
                  });
                }}
              >
                {DUE_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
              {it.due.kind === "before_event_hours" && (
                <input className="nx-input w-24 tabular-nums" type="number" min={1} value={it.due.hours} disabled={disabled} onChange={(e) => update(i, { due: { kind: "before_event_hours", hours: Math.max(1, Number(e.target.value) || 1) } })} />
              )}
              {it.due.kind === "after_event_business_days" && (
                <input className="nx-input w-24 tabular-nums" type="number" min={1} value={it.due.days} disabled={disabled} onChange={(e) => update(i, { due: { kind: "after_event_business_days", days: Math.max(1, Number(e.target.value) || 1) } })} />
              )}
              <span className="text-xs text-fg-subtle">{dueLabel(it.due)}</span>
            </div>
            <label className="flex items-center gap-2 text-xs text-fg-muted">
              <input type="checkbox" checked={!!it.onlyIfRequested} disabled={disabled} onChange={(e) => update(i, { onlyIfRequested: e.target.checked || undefined })} />
              Sólo si el productor lo pide (parte como "no aplica")
            </label>
          </li>
        ))}
      </ol>
      {!disabled && (
        <button
          type="button"
          className="nx-btn-ghost"
          onClick={() => onChange([...items, { key: slugKey("nuevo item", items.map((x) => x.key)), label: "Nuevo ítem", help: "", due: { kind: "none" } }])}
        >
          <Plus size={14} /> Agregar ítem
        </button>
      )}
    </div>
  );
}
